import { useEffect, useRef, useState, type DragEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Check,
  ChevronRight,
  ImagePlus,
  Loader2,
  Pencil,
  Play,
  Plus,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { ROUTES } from '@/app/routes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ContentInfrastructureHealth } from '@/features/content/components/ContentInfrastructureHealth';
import { cn } from '@/lib/utils';
import {
  isLabRunInFlight,
  uploadLabMedia,
  useAddLabFixture,
  useDeleteLabFixture,
  useLabCase,
  useLabRuns,
  useTriggerLabRun,
  useUpdateLabCase,
} from '../labApi';
import { LabMediaThumb } from '../components/LabMediaThumb';
import { formatDurationMs } from '../lib/formatDurationMs';
import { useTickingNow } from '../lib/useTickingNow';
import { isHeicLike, prepareLabUploadMedia } from '../lib/prepareLabUploadMedia';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function PipelineLabCasePage() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const { data: labCase, isLoading } = useLabCase(caseId);
  const { data: runs, isPending: runsPending } = useLabRuns(caseId);
  const addFixture = useAddLabFixture(caseId ?? '');
  const deleteFixture = useDeleteLabFixture(caseId ?? '');
  const triggerRun = useTriggerLabRun(caseId ?? '');
  const updateCase = useUpdateLabCase(caseId ?? '');

  const activeRun = (runs ?? []).find((r) => isLabRunInFlight(r));
  const runBusy = triggerRun.isPending || (Boolean(caseId) && runsPending) || Boolean(activeRun);
  const nowMs = useTickingNow(runBusy);
  const activeElapsedMs =
    activeRun?.startedAt != null
      ? Math.max(0, nowMs - new Date(activeRun.startedAt).getTime())
      : null;

  const [caption, setCaption] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [preparingFile, setPreparingFile] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const mediaBusy = preparingFile || uploading;

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const clearSelectedFile = () => {
    setFile(null);
    setPreviewUrl(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const onPickFile = async (picked: File | null) => {
    clearSelectedFile();
    if (!picked) return;
    try {
      setPreparingFile(true);
      const prepared = await prepareLabUploadMedia(picked);
      setFile(prepared);
      if (prepared.type.startsWith('image/') || prepared.type.startsWith('video/')) {
        setPreviewUrl(URL.createObjectURL(prepared));
      }
      if (isHeicLike(picked) && prepared.type === 'image/jpeg') {
        toast.message('Converted HEIC → JPEG (same as mobile upload)');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to prepare media');
      clearSelectedFile();
    } finally {
      setPreparingFile(false);
    }
  };

  const onDragEnterMedia = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const onDragOverMedia = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const onDragLeaveMedia = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setDragOver(false);
  };

  const onDropMedia = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (mediaBusy) return;
    const dropped = e.dataTransfer.files?.[0] ?? null;
    void onPickFile(dropped);
  };

  const [editingMeta, setEditingMeta] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');

  useEffect(() => {
    if (!labCase || editingMeta) return;
    setEditName(labCase.name);
    setEditDescription(labCase.description ?? '');
  }, [labCase, editingMeta]);

  if (!caseId) {
    return <p className="p-6 text-sm text-destructive">Missing case id</p>;
  }

  const startEditMeta = () => {
    setEditName(labCase?.name ?? '');
    setEditDescription(labCase?.description ?? '');
    setEditingMeta(true);
  };

  const cancelEditMeta = () => {
    setEditName(labCase?.name ?? '');
    setEditDescription(labCase?.description ?? '');
    setEditingMeta(false);
  };

  const saveEditMeta = async () => {
    if (!editName.trim()) {
      toast.error('Name is required');
      return;
    }
    try {
      await updateCase.mutateAsync({
        name: editName.trim(),
        description: editDescription,
      });
      setEditingMeta(false);
      toast.success('Test case updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update case');
    }
  };

  const onAddFixture = async () => {
    if (!caption.trim() && !file) {
      toast.error('Add a caption and/or media');
      return;
    }
    try {
      setUploading(true);
      const body: {
        caption?: string;
        mediaS3Key?: string;
        mediaContentType?: string;
      } = {};
      if (caption.trim()) body.caption = caption.trim();
      if (file) {
        const uploaded = await uploadLabMedia(file);
        body.mediaS3Key = uploaded.s3Key;
        body.mediaContentType = uploaded.contentType;
      }
      await addFixture.mutateAsync(body);
      setCaption('');
      clearSelectedFile();
      toast.success('Fixture added');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add fixture');
    } finally {
      setUploading(false);
    }
  };

  const onRun = async () => {
    if (runBusy) {
      toast.message(
        activeRun
          ? `Run #${activeRun.id} is still in progress`
          : 'A run is already starting…',
      );
      if (activeRun) {
        void navigate(ROUTES.pipelineLabRun(caseId, String(activeRun.id)));
      }
      return;
    }
    try {
      const run = await triggerRun.mutateAsync();
      toast.message(`Run #${run.id} started — GPU + Cloud can take a few minutes`);
      void navigate(ROUTES.pipelineLabRun(caseId, String(run.id)));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Run failed');
    }
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <nav className="flex items-center gap-1 text-sm text-muted-foreground">
        <Link to={ROUTES.moderation} className="hover:text-foreground">
          Moderation
        </Link>
        <ChevronRight className="size-4" />
        <Link to={ROUTES.pipelineLab} className="hover:text-foreground">
          Pipeline Lab
        </Link>
        <ChevronRight className="size-4" />
        <span className="text-foreground">{labCase?.name ?? caseId}</span>
      </nav>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !labCase ? (
        <p className="text-sm text-destructive">Case not found</p>
      ) : (
        <>
          <div className="flex flex-wrap items-start justify-between gap-3">
            {editingMeta ? (
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <label className="flex flex-col gap-1 text-sm">
                  Name
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void saveEditMeta();
                      if (e.key === 'Escape') cancelEditMeta();
                    }}
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  Description
                  <Input
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    placeholder="Optional notes"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void saveEditMeta();
                      if (e.key === 'Escape') cancelEditMeta();
                    }}
                  />
                </label>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => void saveEditMeta()}
                    disabled={updateCase.isPending}
                  >
                    {updateCase.isPending ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Check className="size-4" />
                    )}
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={cancelEditMeta}
                    disabled={updateCase.isPending}
                  >
                    <X className="size-4" />
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="min-w-0 flex-1">
                <div className="flex items-start gap-2">
                  <h1 className="text-2xl font-semibold tracking-tight">{labCase.name}</h1>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="mt-0.5 size-8 shrink-0"
                    aria-label="Edit test case name"
                    onClick={startEditMeta}
                  >
                    <Pencil className="size-4" />
                  </Button>
                </div>
                {labCase.description ? (
                  <p className="mt-1 text-sm text-muted-foreground">{labCase.description}</p>
                ) : (
                  <button
                    type="button"
                    className="mt-1 text-sm text-muted-foreground underline-offset-2 hover:underline"
                    onClick={startEditMeta}
                  >
                    Add description
                  </button>
                )}
              </div>
            )}
            <div className="flex flex-col items-stretch gap-3 sm:items-end">
            <ContentInfrastructureHealth className="h-auto bg-zinc-950 text-zinc-100 shadow-none" />
            <Button
              className="self-end"
              onClick={() => void onRun()}
              disabled={runBusy || !(labCase.fixtures?.length)}
            >
              {runBusy ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Play className="size-4" />
              )}
              {activeRun ? `Running #${activeRun.id}…` : 'Run GPU + Cloud'}
            </Button>
            </div>
          </div>

          {activeRun ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3">
              <div className="flex min-w-0 items-center gap-2 text-sm">
                <Loader2 className="size-4 shrink-0 animate-spin text-primary" />
                <span>
                  Run #{activeRun.id} in progress
                  {activeElapsedMs != null
                    ? ` · ${formatDurationMs(activeElapsedMs)}`
                    : ''}
                </span>
              </div>
              <Button
                size="sm"
                variant="secondary"
                onClick={() =>
                  void navigate(ROUTES.pipelineLabRun(caseId, String(activeRun.id)))
                }
              >
                View run
              </Button>
            </div>
          ) : null}

          <section className="rounded-lg border bg-card p-4">
            <h2 className="mb-3 text-sm font-medium">Add fixture</h2>
            <div className="flex flex-col gap-4">
              <input
                ref={fileRef}
                type="file"
                accept="image/*,video/*,.heic,.heif"
                className="sr-only"
                disabled={mediaBusy}
                onChange={(e) => void onPickFile(e.target.files?.[0] ?? null)}
              />

              {file ? (
                <div
                  className={cn(
                    'relative overflow-hidden rounded-lg border bg-muted/30 transition-colors',
                    dragOver && 'border-primary bg-primary/5',
                  )}
                  onDragEnter={onDragEnterMedia}
                  onDragOver={onDragOverMedia}
                  onDragLeave={onDragLeaveMedia}
                  onDrop={onDropMedia}
                >
                  <div className="flex flex-col gap-3 p-3 sm:flex-row sm:items-stretch">
                    <div className="relative mx-auto aspect-square w-full max-w-[180px] shrink-0 sm:mx-0">
                      {previewUrl ? (
                        <LabMediaThumb
                          previewUrl={previewUrl}
                          mediaContentType={file.type}
                          className="size-full max-w-none rounded-md"
                        />
                      ) : (
                        <div className="flex size-full items-center justify-center rounded-md bg-muted text-muted-foreground">
                          <Upload className="size-8" />
                        </div>
                      )}
                      {preparingFile ? (
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-md bg-background/60">
                          <Loader2 className="size-6 animate-spin text-primary" />
                        </div>
                      ) : dragOver ? (
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-md bg-background/60 text-xs font-medium text-primary">
                          Drop to replace
                        </div>
                      ) : null}
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{file.name}</div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {file.type || 'unknown type'} · {formatFileSize(file.size)}
                        </div>
                        <p className="mt-2 text-xs text-muted-foreground">
                          HEIC is converted to JPEG before upload (same as mobile). Drop a
                          file here to replace.
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={mediaBusy}
                          onClick={() => fileRef.current?.click()}
                        >
                          Replace
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={mediaBusy}
                          onClick={clearSelectedFile}
                        >
                          <X className="size-4" />
                          Remove
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  disabled={mediaBusy}
                  onClick={() => fileRef.current?.click()}
                  onDragEnter={onDragEnterMedia}
                  onDragOver={onDragOverMedia}
                  onDragLeave={onDragLeaveMedia}
                  onDrop={onDropMedia}
                  className={cn(
                    'flex min-h-[160px] w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-8 text-center transition-colors',
                    'hover:border-primary/50 hover:bg-muted/40',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    'disabled:pointer-events-none disabled:opacity-60',
                    dragOver
                      ? 'border-primary bg-primary/5'
                      : 'border-muted-foreground/30 bg-muted/20',
                  )}
                >
                  {preparingFile ? (
                    <Loader2 className="size-8 animate-spin text-primary" />
                  ) : (
                    <ImagePlus className="size-8 text-muted-foreground" />
                  )}
                  <div className="text-sm font-medium">
                    {preparingFile
                      ? 'Preparing media…'
                      : dragOver
                        ? 'Drop to upload'
                        : 'Drop image or video here'}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    or click to browse · JPEG, PNG, HEIC, MP4…
                  </div>
                </button>
              )}

              <label className="flex flex-col gap-1 text-sm">
                Caption
                <Input
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Post caption reused on every run"
                  disabled={uploading || addFixture.isPending}
                />
              </label>

              <div className="flex flex-wrap items-center gap-3">
                <Button
                  variant="secondary"
                  onClick={() => void onAddFixture()}
                  disabled={uploading || preparingFile || addFixture.isPending}
                >
                  {uploading || preparingFile || addFixture.isPending ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Plus className="size-4" />
                  )}
                  Add fixture
                </Button>
                <p className="text-xs text-muted-foreground">
                  Media is uploaded once and reused. Fixtures never create tipmi posts.
                </p>
              </div>
            </div>
          </section>

          <section className="rounded-lg border bg-card">
            <div className="border-b px-4 py-3 text-sm font-medium">
              Fixtures ({labCase.fixtures?.length ?? 0})
            </div>
            {!labCase.fixtures?.length ? (
              <p className="p-4 text-sm text-muted-foreground">No fixtures yet</p>
            ) : (
              <ul className="divide-y">
                {labCase.fixtures.map((f) => (
                  <li key={f.id} className="flex gap-4 px-4 py-3">
                    <LabMediaThumb
                      previewUrl={f.previewUrl}
                      mediaFileType={f.mediaFileType}
                      mediaContentType={f.mediaContentType}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="whitespace-pre-wrap text-sm">
                        {f.caption?.trim() || (
                          <span className="text-muted-foreground">(no caption)</span>
                        )}
                      </div>
                      <div className="mt-1 truncate text-xs text-muted-foreground">
                        {f.mediaFileType ?? '—'}
                        {f.mediaS3Key ? ` · ${f.mediaS3Key}` : ''}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Delete fixture"
                      onClick={() => {
                        void deleteFixture
                          .mutateAsync(f.id)
                          .then(() => toast.success('Fixture deleted'))
                          .catch((err: unknown) =>
                            toast.error(
                              err instanceof Error ? err.message : 'Delete failed',
                            ),
                          );
                      }}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="rounded-lg border bg-card">
            <div className="border-b px-4 py-3 text-sm font-medium">Past runs</div>
            {!runs?.length ? (
              <p className="p-4 text-sm text-muted-foreground">No runs yet</p>
            ) : (
              <ul className="divide-y">
                {runs.map((r) => {
                  const inFlight = isLabRunInFlight(r);
                  return (
                    <li key={r.id}>
                      <button
                        type="button"
                        className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-muted/40"
                        onClick={() =>
                          void navigate(ROUTES.pipelineLabRun(caseId, String(r.id)))
                        }
                      >
                        <span className="flex items-center gap-2 text-sm">
                          {inFlight ? (
                            <Loader2 className="size-3.5 animate-spin text-primary" />
                          ) : null}
                          Run #{r.id} · {r.status}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {r.startedAt ? new Date(r.startedAt).toLocaleString() : ''}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
